<!-- Ported from the Polaris kit: src/product/components/Inspector.vue.
     Classes translated to Tailwind 3 by rebranding_frontend/port-kit-component.mjs;
     strings moved to i18n by hand. Change the design in the kit first. -->
<script setup>
// A tabbed side panel about the thing on screen. With room it is a column next to the content; without room
// (`overlay`) the same panel opens as a registry Sheet. One slot per tab, named after the tab's value.
import { createReusableTemplate } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { Close16 } from '@carbon/icons-vue';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

defineProps({
  open: Boolean,
  overlay: Boolean,
  label: { type: String, required: true },
  tabs: { type: Array, required: true },
  tab: { type: String, required: true },
});
const emit = defineEmits(['update:open', 'update:tab']);
const { t } = useI18n();
// Several root nodes: a class from the parent (a width, for example) goes to the column, the only form the parent lays out.
defineOptions({ inheritAttrs: false });
const [DefinePanel, Panel] = createReusableTemplate();
</script>

<template>
  <DefinePanel>
    <Tabs
      :model-value="tab"
      class="min-h-0 flex-1 gap-0"
      @update:model-value="value => emit('update:tab', value)"
    >
      <!-- In a sheet the registry close button sits at the top right, so the header leaves room for it. -->
      <header
        class="flex h-[4.5rem] shrink-0 items-center justify-between gap-2 border-b border-solid border-polaris-border px-4"
        :class="overlay ? 'pr-12' : ''"
      >
        <!-- Each tab is as wide as its label (not the registry's equal flex-1), from the start of the header, and a label is
             never cut: "Ask Polaris" in a longer language needs more room than "Details". If one day they do not fit, the
             list scrolls sideways (the arrow keys bring a tab into view) and the close button keeps its place.
             The kit's `scrollbar-none` is this app's `no-scrollbar` (_woot.scss). -->
        <TabsList
          :aria-label="label"
          class="min-w-0 justify-start overflow-x-auto no-scrollbar"
        >
          <TabsTrigger
            v-for="tabItem in tabs"
            :key="tabItem.value"
            :value="tabItem.value"
            class="flex-none"
          >
            {{ tabItem.label }}
          </TabsTrigger>
        </TabsList>
        <Button
          v-if="!overlay"
          variant="ghost"
          size="icon-sm"
          :aria-label="
            t('POLARIS.INSPECTOR.CLOSE', { label: label.toLowerCase() })
          "
          @click="emit('update:open', false)"
        >
          <Close16 aria-hidden="true" />
        </Button>
      </header>
      <!-- Tailwind 3's preflight `[hidden]` loses to `flex` (Tailwind 4's is !important), so the inactive
           panel says it is hidden by its state, or it takes half the height. -->
      <TabsContent
        v-for="tabItem in tabs"
        :key="tabItem.value"
        :value="tabItem.value"
        class="flex min-h-0 flex-1 flex-col overflow-auto data-[state=inactive]:hidden"
      >
        <slot :name="tabItem.value" />
      </TabsContent>
    </Tabs>
  </DefinePanel>

  <Sheet
    v-if="overlay"
    :open="open"
    @update:open="value => emit('update:open', value)"
  >
    <SheetContent class="w-full gap-0 sm:max-w-sm">
      <SheetTitle class="sr-only">{{ label }}</SheetTitle>
      <SheetDescription class="sr-only">
        {{ tabs.map(tabItem => tabItem.label).join(', ') }}
      </SheetDescription>
      <Panel />
    </SheetContent>
  </Sheet>
  <aside
    v-else-if="open"
    v-bind="$attrs"
    :aria-label="label"
    class="flex min-h-0 min-w-0 flex-col border-l border-solid border-polaris-border bg-polaris-card"
  >
    <Panel />
  </aside>
</template>
